import React, { useState, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import nullLogo from "../../assets/images/null.png";
import owaspLogo from "../../assets/images/owasp.png";
import bcsLogo from "../../assets/images/bcs.png";
import hbrLogo from "../../assets/images/hbr.png";
import bugLogo from "../../assets/images/bug.png";
import appsecLogo from "../../assets/images/appsec.png";
import stanfordLogo from "../../assets/images/stanford.png";
import packtLogo from "../../assets/images/packt.jpeg";

const volunteerExperiences = [
  {
    title: "Project Lead & Creator",
    company: "OWASP VXDF (Validated Exploitable Data Flow)",
    logo: owaspLogo,
    date: "2023 - Present",
    details: [
      "Leading the development of OWASP VXDF - an open-source JSON standard for documenting confirmed security vulnerabilities",
      "Building a community of security professionals and organizations adopting the standard",
      "Developing reference implementations and SDKs for multiple programming languages",
      "Collaborating with security vendors to integrate VXDF into their products",
      "Working under OWASP umbrella to ensure industry-wide adoption and standardization",
    ],
    link: "https://vxdf.org",
    highlight: true,
    gradient: "from-blue-400 to-purple-400",
  },
  {
    title: "Advisory Board Member",
    company: "Packt",
    logo: packtLogo,
    date: "2023 - Present",
    details: [
      "Asked to join the Advisory Board for the company, advising and providing strategic guidance in latest security trends in the Industry and shaping the future of the company",
    ],
    gradient: "from-orange-400 to-red-400",
  },
  {
    title: "Industry Mentor",
    company: "Stanford University",
    logo: stanfordLogo,
    date: "2020 - Present",
    details: [
      "Guided mentees through research and capstone projects in web app security.",
      "Supplemented course structure with industry implementations of security concepts.",
      "Mentored professionals and students in cybersecurity practices and research.",
    ],
    gradient: "from-red-400 to-pink-400",
  },
  {
    title: "Fellowship Assessor",
    company: "British Computer Society",
    logo: bcsLogo,
    date: "2023 - 2025",
    details: [
      "Nominated as an FBCS assessor, in which capacity, I assess the applications for aspiring members of the BCS under the Fellowship category and admit them upon a successful assessment.",
    ],
    gradient: "from-green-400 to-blue-400",
  },
  {
    title: "Advisory Council Member",
    company: "Harvard Business Review",
    logo: hbrLogo,
    date: "2024 - 2025",
    details: [
      "Helped in research and ongoing activities at HBR as an advisory council member.",
    ],
    gradient: "from-purple-400 to-pink-400",
  },
  {
    title: "Advisor",
    company: "Bugcrowd",
    logo: bugLogo,
    date: "2022 - 2025",
    details: [
      "Review reports and submissions from researchers across the globe and authorize awards based on the merit of the submissions.",
    ],
    gradient: "from-yellow-400 to-orange-400",
  },
  {
    title: "Guest Speaker",
    company: "OWASP AppSec Day",
    logo: appsecLogo,
    date: "2020 - 2022",
    details: [
      "I was an invited speaker at OWASP AppSec conferences across the globe and I have talked and trained the participants on topics:",
      "Best practices in using wide operations platforms including Kubernetes/serverless stack, etc.",
      "Securing the DevOps Code pipeline.",
      "Best security practices and leveraging cloud-native solutions in deploying applications.",
      "Enhancing API performance and security.",
    ],
    gradient: "from-cyan-400 to-blue-400",
  },
  {
    title: "Core Committee Member",
    company: "null Security",
    logo: nullLogo,
    date: "2017 - 2024",
    details: [
      "Managed organizational strategic planning and community development.",
      "Guided and mentored new community members in cybersecurity practices.",
      "Assessed applications for chapter leadership and community involvement.",
    ],
    gradient: "from-gray-400 to-gray-600",
  },
  {
    title: "Chapter Leader",
    company: "OWASP Bangalore",
    logo: owaspLogo,
    date: "2017 - 2021",
    details: [
      "Managed the OWASP Bangalore chapter, making strategic decisions for community growth.",
      "Organized and led numerous community events focused on cloud and application security.",
    ],
    gradient: "from-indigo-400 to-purple-400",
  },
];

const VolunteerExperience = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const controls = useAnimation();

  const { ref: timelineRef, inView: timelineInView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (timelineInView) {
      controls.start({ height: "100%" });
    }
  }, [timelineInView, controls]);

  return (
    <section className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 py-20 relative">
      <div className="container mx-auto px-4">
        <motion.h1
          className="text-4xl sm:text-5xl font-bold text-center mb-12 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Volunteer Experience
        </motion.h1>

        <div className="relative">
          {/* Vertical Line */}
          <motion.div
            ref={timelineRef}
            className="absolute w-0.5 bg-gradient-to-b from-blue-400 via-purple-400 to-pink-400 h-full left-1/2 transform -translate-x-1/2 hidden sm:block"
            initial={{ height: "0%" }}
            animate={controls}
            transition={{ duration: 1.5 }}
          ></motion.div>

          {volunteerExperiences.map((experience, index) => (
            <motion.div
              key={index}
              className={`flex flex-col sm:flex-row items-center justify-between mb-10 w-full ${
                index % 2 === 0 ? "sm:flex-row" : "sm:flex-row-reverse"
              }`}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              onHoverStart={() => setCurrentStep(index + 1)}
            >
              {/* Experience Card */}
              <motion.div
                className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl shadow-xl p-6 sm:w-5/12 w-full mb-4 sm:mb-0 hover:bg-white/10 transition-all duration-300 ${
                  experience.highlight ? "border-2 border-blue-400" : ""
                }`}
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-center mb-4">
                  {/* Company Logo */}
                  <div className="bg-white/10 p-2 rounded-lg mr-4">
                    <img
                      src={experience.logo}
                      alt={`${experience.company} logo`}
                      className="w-12 h-12"
                    />
                  </div>
                  <div>
                    <h2
                      className={`text-xl sm:text-2xl font-semibold mb-1 bg-gradient-to-r ${experience.gradient} bg-clip-text text-transparent`}
                    >
                      {experience.title}
                    </h2>
                    <h3 className="text-lg sm:text-xl font-medium text-gray-300">
                      {experience.company}
                    </h3>
                    <p className="text-sm text-gray-400">{experience.date}</p>
                  </div>
                </div>
                <ul className="text-gray-300 space-y-2 text-sm sm:text-base">
                  {experience.details.map((detail, i) => (
                    <li key={i} className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
                {experience.link && (
                  <motion.a
                    href={experience.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center mt-4 text-blue-400 hover:text-blue-300 transition-colors"
                    whileHover={{ x: 5 }}
                  >
                    Learn more about VXDF →
                  </motion.a>
                )}
              </motion.div>

              {/* Step Marker */}
              <motion.div
                className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-r ${
                  currentStep === index + 1
                    ? "from-yellow-400 to-orange-400"
                    : experience.gradient
                } border-4 border-gray-800 flex items-center justify-center text-white font-bold text-sm sm:text-lg shadow-lg`}
                whileHover={{ scale: 1.2, rotate: 360 }}
                transition={{ duration: 0.5 }}
              >
                {index + 1}
              </motion.div>

              {/* Empty space for alignment */}
              <div className="hidden sm:block sm:w-5/12"></div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default VolunteerExperience;
